import { CreateDoctorPayload, DoctorRepository } from "@/domain/repository/doctor-repository";
import { DEFAULT_PHONE_COUNTRY_CODE, formatPhoneForServer, validatePhoneNumber } from "@/utils/phone-number-utils";
import { SATError, SATErrorType, SATResult } from "@sadtx/sat-error";
import { translate } from "@/utils/i18n";

export type CreateDoctorValidationErrors = Partial<Record<keyof CreateDoctorPayload, string>>;

export class CreateDoctorValidationError extends SATError {
  constructor(public readonly fieldErrors: CreateDoctorValidationErrors) {
    super(SATErrorType.ValidationError, translate("doctor.validation.title"));
  }
}

export class CreateMedicalExpertUsecase {
  constructor(protected readonly doctorRepo: DoctorRepository) {}

  validate(payload: CreateDoctorPayload): CreateDoctorValidationErrors {
    const errors: CreateDoctorValidationErrors = {};
    const fullName = payload.fullName.trim();
    const email = payload.email.trim().toLowerCase();
    const phone = payload.phone?.trim() ?? "";
    const countryCode = payload.countryCode?.trim() || DEFAULT_PHONE_COUNTRY_CODE;
    const hospitalCode = payload.hospitalCode.trim();

    errors.fullName = validateNameBeforeSubmit(fullName, translate("doctor.nameLabel"));

    if (!email) {
      errors.email = translate("doctor.validation.emailRequired");
    } else if (!/^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
      errors.email = translate("doctor.validation.emailFormat");
    }

    if (phone) {
      const phoneError = validatePhoneNumber(phone, countryCode);
      if (phoneError) errors.phone = phoneError;
    }

    if (!hospitalCode) {
      errors.hospitalCode = translate("doctor.validation.hospitalRequired");
    }

    return Object.fromEntries(
      Object.entries(errors).filter(([, message]) => Boolean(message)),
    ) as CreateDoctorValidationErrors;
  }

  async execute(payload: CreateDoctorPayload): Promise<SATResult<void, SATError>> {
    const validationErrors = this.validate(payload);
    if (Object.keys(validationErrors).length > 0) {
      return SATResult.failure(new CreateDoctorValidationError(validationErrors));
    }

    return this.doctorRepo.createMedicalExpert(normalizeCreateDoctorPayload(payload));
  }
}

export function normalizeCreateDoctorPayload(payload: CreateDoctorPayload): CreateDoctorPayload {
  const phone = payload.phone?.trim() ?? "";
  const countryCode = payload.countryCode?.trim() || DEFAULT_PHONE_COUNTRY_CODE;
  return {
    fullName: payload.fullName.trim(),
    email: payload.email.trim().toLowerCase(),
    ...(phone
      ? {
          phone: formatPhoneForServer(phone, countryCode),
        }
      : {}),
    hospitalCode: payload.hospitalCode.trim(),
    memo: payload.memo.trim(),
  };
}

function validateNameBeforeSubmit(value: string, label: string): string | undefined {
  if (!value) return translate("nameField.required", { label });
  if (value.length < 2) return translate("nameField.minLength", { label });
  return validateNameFormat(value, label);
}

function validateNameFormat(value: string, label: string): string | undefined {
  if (/\d/.test(value)) return translate("nameField.noNumber", { label });
  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(value)) {
    return translate("nameField.noSpecialCharacter", { label });
  }
  if (!/^[가-힣a-zA-Z\s]+$/.test(value)) {
    return translate("nameField.charset", { label });
  }
  return undefined;
}
